import { useState, useEffect } from "react";
import { T } from "../../theme";
import { apiCall } from "../../apiClient";
import { Btn } from "../primitives";

export const BusinessUnitManager = () => {
  const [units, setUnits] = useState<any[]>([]);
  const [newName, setNewName] = useState('');
  const [newParent, setNewParent] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editParent, setEditParent] = useState<string>('');

  const load = async () => {
    setLoading(true);
    const r = await apiCall('/settings/business-units');
    if (r?.businessUnits) setUnits(r.businessUnits);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newName.trim()) return;
    const r = await apiCall('/settings/business-units', { method: 'POST', body: JSON.stringify({ name: newName.trim(), parent_business_unit_id: newParent || null }) });
    if (r?.businessUnit) { setNewName(''); setNewParent(''); load(); } else { alert(r?.error || 'Failed'); }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this business unit? Its children will become top-level.')) return;
    await apiCall(`/settings/business-units/${id}`, { method: 'DELETE' });
    load();
  };

  const saveParent = async (id: string) => {
    const r = await apiCall(`/settings/business-units/${id}`, { method: 'PUT', body: JSON.stringify({ parent_business_unit_id: editParent || null }) });
    if (r?.businessUnit) { setEditingId(null); setEditParent(''); load(); }
    else alert(r?.error || 'Failed');
  };

  // Collect descendant ids for an edit target so we can exclude them from
  // its "Parent" dropdown (prevents obvious cycles in the UI before the
  // backend validation kicks in).
  const descendantsOf = (id: string): Set<string> => {
    const out = new Set<string>([id]);
    let added = true;
    while (added) {
      added = false;
      for (const u of units) {
        if (u.parent_business_unit_id && out.has(u.parent_business_unit_id) && !out.has(u.id)) {
          out.add(u.id); added = true;
        }
      }
    }
    return out;
  };

  // Build tree for indented render.
  const children: Record<string, any[]> = {};
  units.forEach(u => {
    const k = u.parent_business_unit_id || 'ROOT';
    (children[k] = children[k] || []).push(u);
  });

  const renderNode = (u: any, depth: number): any => (
    <div key={u.id}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', border: `1px solid ${T.brd}`, borderRadius: 5, marginBottom: 4, marginLeft: depth * 24, backgroundColor: depth > 0 ? T.bg2 : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
          {depth > 0 && <span style={{ color: T.txD, fontSize: 11 }}>└</span>}
          <span style={{ fontSize: 12, color: T.tx, fontWeight: depth === 0 ? 600 : 400 }}>{u.name}</span>
        </div>
        {editingId === u.id ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <select value={editParent} onChange={e => setEditParent(e.target.value)} style={{ padding: '4px 8px', fontSize: 11, border: `1px solid ${T.brd}`, borderRadius: 4, backgroundColor: T.bg3, color: T.tx, fontFamily: 'inherit' }}>
              <option value="">(top-level)</option>
              {units.filter(o => !descendantsOf(u.id).has(o.id)).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <Btn v="primary" sz="sm" onClick={() => { saveParent(u.id); }}>Save</Btn>
            <Btn v="ghost" sz="sm" onClick={() => { setEditingId(null); setEditParent(''); }}>Cancel</Btn>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 6 }}>
            <Btn v="ghost" sz="sm" onClick={() => { setEditingId(u.id); setEditParent(u.parent_business_unit_id || ''); }}>Re-parent</Btn>
            <Btn v="ghost" sz="sm" onClick={() => remove(u.id)} style={{ color: T.red }}>Delete</Btn>
          </div>
        )}
      </div>
      {(children[u.id] || []).sort((a, b) => a.name.localeCompare(b.name)).map(c => renderNode(c, depth + 1))}
    </div>
  );

  if (loading) return <div style={{ padding: 20, color: T.txD }}>Loading...</div>;

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Business Units</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()}
          placeholder="New business unit name..." style={{ flex: 1, minWidth: 180, padding: '8px 10px', border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 12, backgroundColor: T.bg3, color: T.tx, fontFamily: 'inherit', outline: 'none' }} />
        <select value={newParent} onChange={e => setNewParent(e.target.value)} style={{ padding: '8px 10px', fontSize: 12, border: `1px solid ${T.brd}`, borderRadius: 5, backgroundColor: T.bg3, color: T.tx, fontFamily: 'inherit', minWidth: 160 }}>
          <option value="">(top-level)</option>
          {units.map(u => <option key={u.id} value={u.id}>Parent: {u.name}</option>)}
        </select>
        <Btn v="primary" sz="sm" onClick={() => { add(); }}>Add</Btn>
      </div>
      {(children['ROOT'] || []).sort((a, b) => a.name.localeCompare(b.name)).map(u => renderNode(u, 0))}
    </div>
  );
};
